import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useFieldTooltips } from "../contexts/FieldTooltipContext";

const InlineFieldTooltip = ({ page, section = "general", fieldKey }) => {
  const wrapperRef = useRef(null);
  const textareaRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const { user } = useAuth();
  const { ensurePageLoaded, getTooltipText, saveTooltip } = useFieldTooltips();

  const isAdmin = useMemo(
    () =>
      user?.role === "admin" ||
      user?.bridgebudgetRole === "admin" ||
      user?.getBridgeBudgetRole?.() === "admin",
    [user],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const tooltipText = getTooltipText(page, section, fieldKey);

  useEffect(() => {
    ensurePageLoaded(page);
  }, [ensurePageLoaded, page]);

  useEffect(() => {
    if (!isEditing) return;
    if (!textareaRef.current) return;
    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(
      textareaRef.current.value.length,
      textareaRef.current.value.length,
    );
  }, [isEditing]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
        setIsEditing(false);
        setSaveError("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", onClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const startEditing = () => {
    if (!isAdmin) return;
    setDraft(tooltipText || "");
    setSaveError("");
    setIsEditing(true);
  };

  const commit = async () => {
    if (!isAdmin) return;
    const normalizedDraft = String(draft || "").trim();
    const normalizedCurrent = String(tooltipText || "").trim();

    if (normalizedDraft === normalizedCurrent) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      setSaveError("");
      await saveTooltip({
        page,
        section,
        fieldKey,
        tooltipText: normalizedDraft,
      });
      setIsEditing(false);
    } catch (error) {
      setSaveError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save tooltip",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (isEditing || isSaving) return;

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setSaveError("");
      closeTimeoutRef.current = null;
    }, 180);
  };

  return (
    <span
      ref={wrapperRef}
      className="relative inline-block h-5 w-5 align-middle"
    >
      <button
        type="button"
        aria-label="Field help"
        onClick={() => {
          if (isOpen) {
            if (isEditing || isSaving) return;
            setIsOpen(false);
            setSaveError("");
            return;
          }
          setIsOpen(true);
        }}
        onFocus={handleMouseEnter}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`absolute left-0 top-0 z-10 inline-flex h-full w-full items-center justify-center rounded-full border text-xs font-bold leading-none shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
          isOpen
            ? "border-blue-500 bg-blue-600 text-white"
            : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
        }`}
      >
        ?
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-0 z-20 w-72 rounded-md border border-slate-200 bg-white p-3 pt-7 shadow-lg"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {!isEditing ? (
            <div
              className={`text-xs leading-5 ${isAdmin ? "cursor-text text-slate-700" : "text-slate-600"}`}
              onClick={startEditing}
              title={isAdmin ? "Click to edit tooltip" : undefined}
            >
              {tooltipText ||
                (isAdmin
                  ? "Click to add tooltip text."
                  : "No help text available.")}
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                ref={textareaRef}
                rows={4}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setIsEditing(false);
                    setSaveError("");
                    return;
                  }
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    commit();
                  }
                }}
                placeholder="Enter tooltip text"
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-[11px] text-slate-500">
                Press Enter to save. Shift+Enter for a new line.
              </p>
            </div>
          )}

          {(isSaving || saveError) && (
            <div
              className={`mt-2 text-[11px] ${saveError ? "text-rose-600" : "text-slate-500"}`}
            >
              {saveError || "Saving..."}
            </div>
          )}
        </div>
      )}
    </span>
  );
};

export default InlineFieldTooltip;
